import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Lock, LockKeyhole } from "lucide-react"
import { useState, useEffect } from "react"
import { Link, useSearchParams, useNavigate } from "react-router-dom"
import Logo from "@/components/layout/logo"
import { usePlatformParams } from "@/hooks/use-platform-params"
import { ROUTES } from "@/config/routes"
import { resetPw } from "@/api/auth"
import { toast } from "sonner"

export function PasswordResetPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { platformParams, createUrlWithParams } = usePlatformParams();

  const code = searchParams.get('code');

  useEffect(() => {
    if (!code) {
      toast.error('유효하지 않은 비밀번호 재설정 링크입니다.');
      navigate(createUrlWithParams(ROUTES.SIGNIN));
    }
  }, [code, createUrlWithParams, navigate]);

  const validatePassword = (password: string): boolean => {
    // 영어, 숫자, 특수문자 포함 6자리 이상 검증
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
    return passwordRegex.test(password);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code) {
      toast.error('유효하지 않은 비밀번호 재설정 코드입니다.');
      return;
    }

    if (!password || !confirmPassword) {
      toast.error('비밀번호를 입력해 주세요.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (!validatePassword(password)) {
      toast.error('비밀번호는 영어, 숫자, 특수문자를 포함한 6자리 이상이어야 합니다.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await resetPw(
        { code, password }, 
        platformParams.platform
      );

      if (response.code === 200) {
        toast.success('비밀번호가 성공적으로 재설정되었습니다.');
        navigate(createUrlWithParams(ROUTES.SIGNIN));
      } else {
        toast.error(response.message || '비밀번호 재설정에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 재설정 오류:', error);
      toast.error('비밀번호 재설정 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-2xl">비밀번호 재설정</CardTitle>
          <CardDescription>
            새로운 비밀번호를 입력해주세요.<br />
            (영어, 숫자, 특수문자 포함 6자리 이상)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">새 비밀번호</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="새 비밀번호를 입력하세요"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">새 비밀번호 확인</Label>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="새 비밀번호를 다시 입력하세요"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "처리 중..." : "비밀번호 재설정"}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
            비밀번호가 기억나셨나요?{" "}
            <Link 
              className="text-primary hover:text-primary/80 underline"
              to={createUrlWithParams(ROUTES.SIGNIN)}
            >
              로그인하기
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}